import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { AppWindow, Check, Copy, FileJson, Globe, Link2, Loader, Pencil, Plus, RefreshCw, Search, Shield, Trash2, X } from 'lucide-react';
import { useTheme } from '../../../components/common/Theme';
import { useToast } from '../../../components/common/Toast';
import ConfirmDialog from '../governed-mcp-gateway/components/ConfirmDialog';
import {
  fieldClass,
  ghostBtn,
  labelClass,
  primaryBtn,
} from '../governed-mcp-gateway/components/GatewayUi';
import { oauthAppsApi } from './oauthAppsApi';
import { ALLOWED_SCOPES } from './oauthAppsConstants';
import { emptyOAuthAppForm, formFromApp, type OAuthApp, type OAuthAppFormState, type OAuthSecretReveal } from './oauthAppsTypes';

function formatWhen(value: string): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

function credentialJson(app: OAuthApp, secret?: string | null): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    client_id: app.clientId,
    application_name: app.applicationName,
    redirect_uris: app.redirectUris,
    allowed_scopes: app.scopes,
  };
  if (secret) payload.client_secret = secret;
  return payload;
}

export default function OAuthAppsPage() {
  const { theme } = useTheme();
  const isLight = theme === 'white';
  const { showToast } = useToast();
  const toast = (type: 'success' | 'error' | 'info', message: string) => showToast({ type, message });

  const [apps, setApps] = useState<OAuthApp[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<OAuthApp | null>(null);
  const [form, setForm] = useState<OAuthAppFormState>(emptyOAuthAppForm());
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<OAuthApp | null>(null);
  const [rotating, setRotating] = useState<OAuthApp | null>(null);
  const [reveal, setReveal] = useState<OAuthSecretReveal | null>(null);
  const [jsonApp, setJsonApp] = useState<{ app: OAuthApp; secret?: string | null } | null>(null);
  const [busyId, setBusyId] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setApps(await oauthAppsApi.list());
    } catch (err) {
      toast('error', err instanceof Error ? err.message : 'Unable to load OAuth apps');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return apps;
    return apps.filter((app) =>
      `${app.applicationName} ${app.clientId} ${app.clientType} ${app.scopes.join(' ')}`.toLowerCase().includes(q)
    );
  }, [apps, query]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyOAuthAppForm());
    setFormOpen(true);
  };

  const openEdit = (app: OAuthApp) => {
    setEditing(app);
    setForm(formFromApp(app));
    setFormOpen(true);
  };

  const saveForm = async () => {
    if (!form.applicationName.trim() || form.redirectUris.every((uri) => !uri.trim()) || !form.scopes.length) return;
    setSaving(true);
    try {
      if (editing) {
        const updated = await oauthAppsApi.update(editing.id, form);
        setApps((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
        toast('success', 'OAuth app updated');
      } else {
        const created = await oauthAppsApi.create(form);
        setApps((prev) => [created, ...prev]);
        if (created.clientSecret) {
          setReveal({
            clientId: created.clientId,
            clientSecret: created.clientSecret,
            applicationName: created.applicationName,
            redirectUris: created.redirectUris,
            scopes: created.scopes,
          });
        }
        toast('success', created.clientSecret ? 'App registered. Copy the secret now.' : 'OAuth app registered');
      }
      setFormOpen(false);
    } catch (err) {
      toast('error', err instanceof Error ? err.message : 'Unable to save OAuth app');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    try {
      await oauthAppsApi.remove(deleting.id);
      setApps((prev) => prev.filter((item) => item.id !== deleting.id));
      toast('success', 'OAuth app deleted');
    } catch (err) {
      toast('error', err instanceof Error ? err.message : 'Unable to delete OAuth app');
    } finally {
      setDeleting(null);
    }
  };

  const confirmRotate = async () => {
    if (!rotating) return;
    const app = rotating;
    setRotating(null);
    await rotate(app);
  };

  const rotate = async (app: OAuthApp) => {
    setBusyId(app.id);
    try {
      const secret = await oauthAppsApi.rotateSecret(app.id);
      setApps((prev) =>
        prev.map((item) =>
          item.id === app.id
            ? { ...item, hasClientSecret: true, clientSecretLast4: secret.clientSecret.slice(-4) }
            : item
        )
      );
      setReveal({
        clientId: secret.clientId || app.clientId,
        clientSecret: secret.clientSecret,
        applicationName: app.applicationName,
        redirectUris: app.redirectUris,
        scopes: app.scopes,
      });
      toast('success', 'Secret rotated. Copy it now; it is shown once.');
    } catch (err) {
      toast('error', err instanceof Error ? err.message : 'Unable to rotate secret');
    } finally {
      setBusyId('');
    }
  };

  return (
    <div className={`w-full min-w-0 -m-3 min-h-[calc(100vh-1.5rem)] ${isLight ? 'bg-slate-50' : 'bg-neutral-950'}`}>
      <header className="px-6 pt-6">
        <p className={`m-0 text-[11px] font-semibold uppercase tracking-[0.08em] ${isLight ? 'text-indigo-600' : 'text-indigo-400'}`}>
          Access & System
        </p>
        <h1 className={`mt-1 text-xl font-semibold ${isLight ? 'text-neutral-900' : 'text-neutral-50'}`}>OAuth Apps</h1>
        <p className={`mt-1.5 max-w-2xl text-[13px] ${isLight ? 'text-slate-500' : 'text-neutral-400'}`}>
          Register third-party applications. Client secrets are hashed in Mongo and shown only once.
        </p>
      </header>

      <div className="px-6 py-5">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={15} className={`absolute left-3 top-1/2 -translate-y-1/2 ${isLight ? 'text-neutral-400' : 'text-neutral-500'}`} />
            <input
              className={`${fieldClass(isLight)} pl-9`}
              placeholder="Search apps, client id, or scopes…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <button type="button" className={`${primaryBtn()} rounded-full`} onClick={openCreate}>
            <Plus size={14} />
            Register app
          </button>
        </div>

        {loading ? (
          <div className={`flex items-center justify-center py-24 text-sm ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>
            <Loader size={16} className="animate-spin mr-2" />
            Loading OAuth apps…
          </div>
        ) : rows.length === 0 ? (
          <div className={`rounded-2xl border px-6 py-16 text-center ${isLight ? 'bg-white border-neutral-200' : 'bg-neutral-900 border-neutral-800'}`}>
            <p className={`text-sm font-medium ${isLight ? 'text-neutral-900' : 'text-white'}`}>No OAuth apps yet</p>
            <p className={`text-sm mt-1 ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>
              Register a confidential or public client to get a client id.
            </p>
            <button type="button" className={`${primaryBtn()} rounded-full mt-4`} onClick={openCreate}>
              <Plus size={14} />
              Register app
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {rows.map((app) => (
              <OAuthAppCard
                key={app.id}
                app={app}
                isLight={isLight}
                rotating={busyId === app.id}
                onOpen={() => openEdit(app)}
                onEdit={() => openEdit(app)}
                onJson={() => setJsonApp({ app })}
                onRotate={() => setRotating(app)}
                onDelete={() => setDeleting(app)}
              />
            ))}
          </div>
        )}
      </div>

      {formOpen && (
        <FormModal
          isLight={isLight}
          editing={Boolean(editing)}
          form={form}
          saving={saving}
          onChange={setForm}
          onClose={() => setFormOpen(false)}
          onSave={() => void saveForm()}
        />
      )}

      {reveal && (
        <CredentialsModal
          isLight={isLight}
          reveal={reveal}
          onJson={() => {
            const match = apps.find((item) => item.clientId === reveal.clientId);
            if (match) setJsonApp({ app: match, secret: reveal.clientSecret });
          }}
          onClose={() => setReveal(null)}
        />
      )}

      {jsonApp && (
        <JsonModal isLight={isLight} payload={credentialJson(jsonApp.app, jsonApp.secret)} onClose={() => setJsonApp(null)} />
      )}

      <ConfirmDialog
        open={Boolean(rotating)}
        title="Rotate client secret?"
        description={`A new secret will be created for ${rotating?.applicationName || 'this app'}. The current secret stops working immediately, and the new one is shown only once.`}
        confirmLabel="Rotate secret"
        isLight={isLight}
        onCancel={() => setRotating(null)}
        onConfirm={() => void confirmRotate()}
      />

      <ConfirmDialog
        open={Boolean(deleting)}
        title="Delete OAuth app"
        description={`Delete ${deleting?.applicationName || 'this app'}? Clients using this client id will stop working.`}
        confirmLabel="Delete"
        danger
        isLight={isLight}
        onCancel={() => setDeleting(null)}
        onConfirm={() => void confirmDelete()}
      />
    </div>
  );
}

function OAuthAppCard({
  app,
  isLight,
  rotating,
  onOpen,
  onEdit,
  onJson,
  onRotate,
  onDelete,
}: {
  app: OAuthApp;
  isLight: boolean;
  rotating: boolean;
  onOpen: () => void;
  onEdit: () => void;
  onJson: () => void;
  onRotate: () => void;
  onDelete: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const confidential = app.clientType === 'confidential';
  const TypeIcon = confidential ? Shield : Globe;
  const firstRedirect = app.redirectUris.find((uri) => uri.trim()) || '';
  const extraRedirects = Math.max(0, app.redirectUris.filter((uri) => uri.trim()).length - 1);
  const muted = isLight ? 'text-neutral-500' : 'text-neutral-400';
  const iconBtn = isLight
    ? 'p-1.5 rounded-lg text-neutral-500 hover:text-indigo-600 hover:bg-indigo-50'
    : 'p-1.5 rounded-lg text-neutral-400 hover:text-white hover:bg-neutral-700';

  const copyId = async (event: React.MouseEvent) => {
    event.stopPropagation();
    try {
      await navigator.clipboard.writeText(app.clientId);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1200);
    } catch {
      setCopied(false);
    }
  };

  return (
    <article
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpen();
        }
      }}
      className={`group relative z-0 hover:z-10 flex flex-col rounded-2xl p-5 text-left cursor-pointer transition-all ${
        isLight
          ? 'bg-white shadow-[0_1px_2px_rgba(16,24,40,0.04)] ring-1 ring-black/[0.04] hover:shadow-[0_8px_24px_rgba(16,24,40,0.08)] hover:-translate-y-0.5'
          : 'bg-neutral-900 ring-1 ring-white/10 hover:ring-white/20'
      }`}
    >
      <div className="flex items-start gap-3 min-w-0 pr-16">
        <span
          className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 ${
            confidential
              ? isLight
                ? 'bg-indigo-50 text-indigo-600'
                : 'bg-indigo-500/15 text-indigo-300'
              : isLight
                ? 'bg-sky-50 text-sky-600'
                : 'bg-sky-500/15 text-sky-300'
          }`}
        >
          <AppWindow size={18} />
        </span>
        <div className="min-w-0">
          <h3
            className={`text-[15px] font-semibold tracking-tight truncate ${isLight ? 'text-neutral-900' : 'text-white'}`}
            title={app.applicationName}
          >
            {app.applicationName}
          </h3>
          <p className={`mt-0.5 inline-flex items-center gap-1 text-[11px] font-medium capitalize ${muted}`}>
            <TypeIcon size={11} />
            {app.clientType}
            {confidential && app.clientSecretLast4 ? ` · ••••${app.clientSecretLast4}` : null}
          </p>
        </div>
      </div>

      <div
        className={`mt-4 flex items-center gap-2 rounded-xl px-3 py-2 ${
          isLight ? 'bg-neutral-50' : 'bg-neutral-950/60'
        }`}
      >
        <div className="min-w-0 flex-1">
          <p className={`text-[10px] font-semibold uppercase tracking-wide ${muted}`}>Client ID</p>
          <p
            className={`mt-0.5 truncate font-mono text-[12px] ${isLight ? 'text-neutral-700' : 'text-neutral-200'}`}
            title={app.clientId}
          >
            {app.clientId}
          </p>
        </div>
        <IconTipButton
          label={copied ? 'Copied' : 'Copy client ID'}
          className={iconBtn}
          onClick={(event) => void copyId(event)}
        >
          {copied ? <Check size={14} /> : <Copy size={14} />}
        </IconTipButton>
      </div>

      <p className={`mt-3 flex items-start gap-1.5 text-sm min-h-[1.25rem] ${muted}`}>
        <Link2 size={13} className="mt-0.5 shrink-0" />
        <span className="truncate" title={firstRedirect || undefined}>
          {firstRedirect || 'No redirect URI'}
        </span>
        {extraRedirects ? <span className="shrink-0">+{extraRedirects}</span> : null}
      </p>

      <div className={`mt-4 pt-4 border-t flex flex-wrap gap-1.5 ${isLight ? 'border-neutral-100' : 'border-neutral-800'}`}>
        {app.scopes.length ? (
          app.scopes.slice(0, 4).map((scope) => (
            <span
              key={scope}
              className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-medium ${
                isLight ? 'bg-neutral-100 text-neutral-600' : 'bg-neutral-800 text-neutral-300'
              }`}
            >
              {scope}
            </span>
          ))
        ) : (
          <span className={`text-[11px] ${muted}`}>No scopes</span>
        )}
        {app.scopes.length > 4 ? (
          <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] ${muted}`}>+{app.scopes.length - 4}</span>
        ) : null}
      </div>

      <p className={`mt-3 text-[11px] ${muted}`}>Created {formatWhen(app.createdAt)}</p>

      <div
        className={`absolute top-3.5 right-3.5 flex items-center gap-0.5 rounded-xl p-0.5 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto focus-within:opacity-100 focus-within:pointer-events-auto transition-opacity ${
          isLight ? 'bg-white/90 shadow-sm ring-1 ring-black/5' : 'bg-neutral-800 ring-1 ring-white/10'
        }`}
      >
        <IconTipButton
          label="Edit"
          className={iconBtn}
          onClick={(event) => {
            event.stopPropagation();
            onEdit();
          }}
        >
          <Pencil size={14} />
        </IconTipButton>
        <IconTipButton
          label="View JSON"
          className={iconBtn}
          onClick={(event) => {
            event.stopPropagation();
            onJson();
          }}
        >
          <FileJson size={14} />
        </IconTipButton>
        {confidential ? (
          <IconTipButton
            label="Rotate secret"
            className={iconBtn}
            disabled={rotating}
            onClick={(event) => {
              event.stopPropagation();
              onRotate();
            }}
          >
            {rotating ? <Loader size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          </IconTipButton>
        ) : null}
        <IconTipButton
          label="Delete"
          className={`p-1.5 rounded-lg ${isLight ? 'text-neutral-500 hover:text-red-600 hover:bg-red-50' : 'text-neutral-400 hover:text-red-400 hover:bg-neutral-700'}`}
          onClick={(event) => {
            event.stopPropagation();
            onDelete();
          }}
        >
          <Trash2 size={14} />
        </IconTipButton>
      </div>
    </article>
  );
}

function FormModal({
  isLight,
  editing,
  form,
  saving,
  onChange,
  onClose,
  onSave,
}: {
  isLight: boolean;
  editing: boolean;
  form: OAuthAppFormState;
  saving: boolean;
  onChange: (form: OAuthAppFormState) => void;
  onClose: () => void;
  onSave: () => void;
}) {
  const valid = Boolean(form.applicationName.trim() && form.redirectUris.some((uri) => uri.trim()) && form.scopes.length);
  return createPortal(
    <div className="fixed inset-0 z-[220] flex items-center justify-center p-4">
      <button type="button" className="absolute inset-0 bg-neutral-950/60 backdrop-blur-[3px]" aria-label="Close" onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        className={`relative w-full max-w-lg rounded-2xl border shadow-2xl overflow-hidden ${
          isLight ? 'border-neutral-200 bg-white' : 'border-neutral-800 bg-neutral-950'
        }`}
      >
        <div className="px-5 pt-5 pb-3 flex items-start justify-between">
          <div>
            <h2 className={`text-lg font-semibold ${isLight ? 'text-neutral-900' : 'text-white'}`}>
              {editing ? 'Update OAuth app' : 'Register OAuth app'}
            </h2>
            <p className={`text-sm mt-1 ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>
              Redirect URIs and scopes are stored tenant-wide.
            </p>
          </div>
          <button type="button" className={ghostBtn(isLight)} onClick={onClose}>
            <X size={14} />
          </button>
        </div>
        <div className="px-5 pb-5 space-y-4 max-h-[70vh] overflow-y-auto">
          <div>
            <label className={labelClass(isLight)}>Application name</label>
            <input
              className={fieldClass(isLight)}
              value={form.applicationName}
              onChange={(e) => onChange({ ...form, applicationName: e.target.value })}
              placeholder="Example App"
            />
          </div>
          <div>
            <label className={labelClass(isLight)}>Redirect URIs</label>
            <div className="space-y-2">
              {form.redirectUris.map((uri, index) => (
                <div key={index} className="flex gap-2">
                  <input
                    className={fieldClass(isLight)}
                    value={uri}
                    onChange={(e) => {
                      const next = [...form.redirectUris];
                      next[index] = e.target.value;
                      onChange({ ...form, redirectUris: next });
                    }}
                    placeholder="https://example.com/auth/callback"
                  />
                  {form.redirectUris.length > 1 && (
                    <button
                      type="button"
                      className={ghostBtn(isLight)}
                      onClick={() => onChange({ ...form, redirectUris: form.redirectUris.filter((_, i) => i !== index) })}
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              ))}
              <button type="button" className={ghostBtn(isLight)} onClick={() => onChange({ ...form, redirectUris: [...form.redirectUris, ''] })}>
                <Plus size={14} />
                Add URI
              </button>
            </div>
          </div>
          <div>
            <label className={labelClass(isLight)}>Scopes</label>
            <div className="flex flex-wrap gap-2">
              {ALLOWED_SCOPES.map((scope) => {
                const checked = form.scopes.includes(scope);
                return (
                  <label
                    key={scope}
                    className={`inline-flex items-center gap-2 rounded-xl border px-3 py-1.5 text-xs cursor-pointer ${
                      checked
                        ? isLight
                          ? 'border-indigo-200 bg-indigo-50 text-indigo-700'
                          : 'border-indigo-500/40 bg-indigo-500/10 text-indigo-200'
                        : isLight
                          ? 'border-neutral-200 text-neutral-600'
                          : 'border-neutral-700 text-neutral-300'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() =>
                        onChange({
                          ...form,
                          scopes: checked ? form.scopes.filter((item) => item !== scope) : [...form.scopes, scope],
                        })
                      }
                    />
                    {scope}
                  </label>
                );
              })}
            </div>
          </div>
          {!editing && (
            <div>
              <label className={labelClass(isLight)}>Client type</label>
              <select
                className={fieldClass(isLight)}
                value={form.clientType}
                onChange={(e) => onChange({ ...form, clientType: e.target.value as OAuthAppFormState['clientType'] })}
              >
                <option value="confidential">Confidential</option>
                <option value="public">Public</option>
              </select>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" className={ghostBtn(isLight)} onClick={onClose}>
              Cancel
            </button>
            <button type="button" className={primaryBtn()} disabled={!valid || saving} onClick={onSave}>
              {saving ? <Loader size={14} className="animate-spin" /> : null}
              {editing ? 'Save changes' : 'Register'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

function CredentialsModal({
  isLight,
  reveal,
  onJson,
  onClose,
}: {
  isLight: boolean;
  reveal: OAuthSecretReveal;
  onJson: () => void;
  onClose: () => void;
}) {
  return createPortal(
    <div className="fixed inset-0 z-[230] flex items-center justify-center p-4">
      <button type="button" className="absolute inset-0 bg-neutral-950/60 backdrop-blur-[3px]" aria-label="Close" onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        className={`relative w-full max-w-lg rounded-2xl border shadow-2xl overflow-hidden ${
          isLight ? 'border-neutral-200 bg-white' : 'border-neutral-800 bg-neutral-950'
        }`}
      >
        <div className="px-5 pt-5 pb-3 flex items-start justify-between">
          <div>
            <h2 className={`text-lg font-semibold ${isLight ? 'text-neutral-900' : 'text-white'}`}>Client credentials</h2>
            <p className={`text-sm mt-1 ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>
              Copy the secret now. Selfic stores only a hash.
            </p>
          </div>
          <button type="button" className={ghostBtn(isLight)} onClick={onClose}>
            <X size={14} />
          </button>
        </div>
        <div className="px-5 pb-5 space-y-3">
          <CopyField isLight={isLight} label="client_id" value={reveal.clientId} />
          <CopyField isLight={isLight} label="client_secret" value={reveal.clientSecret} />
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" className={ghostBtn(isLight)} onClick={onJson}>
              <FileJson size={14} />
              View JSON
            </button>
            <button type="button" className={primaryBtn()} onClick={onClose}>
              Done
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

function JsonModal({
  isLight,
  payload,
  onClose,
}: {
  isLight: boolean;
  payload: Record<string, unknown>;
  onClose: () => void;
}) {
  const text = JSON.stringify(payload, null, 2);
  return createPortal(
    <div className="fixed inset-0 z-[240] flex items-center justify-center p-4">
      <button type="button" className="absolute inset-0 bg-neutral-950/60 backdrop-blur-[3px]" aria-label="Close" onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        className={`relative w-full max-w-xl rounded-2xl border shadow-2xl overflow-hidden ${
          isLight ? 'border-neutral-200 bg-white' : 'border-neutral-800 bg-neutral-950'
        }`}
      >
        <div className="px-5 pt-5 pb-3 flex items-start justify-between">
          <h2 className={`text-lg font-semibold ${isLight ? 'text-neutral-900' : 'text-white'}`}>Application JSON</h2>
          <button type="button" className={ghostBtn(isLight)} onClick={onClose}>
            <X size={14} />
          </button>
        </div>
        <div className="px-5 pb-5">
          <pre
            className={`rounded-xl border p-4 text-xs overflow-auto max-h-[60vh] ${
              isLight ? 'bg-neutral-50 border-neutral-200 text-neutral-800' : 'bg-neutral-900 border-neutral-800 text-neutral-200'
            }`}
          >
            {text}
          </pre>
          <div className="flex justify-end gap-2 mt-3">
            <button
              type="button"
              className={ghostBtn(isLight)}
              onClick={() => void navigator.clipboard.writeText(text)}
            >
              <Copy size={14} />
              Copy
            </button>
            <button type="button" className={primaryBtn()} onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

function IconTipButton({
  label,
  className,
  disabled,
  onClick,
  children,
}: {
  label: string;
  className: string;
  disabled?: boolean;
  onClick: (event: React.MouseEvent) => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      className={`relative group/tip ${className}`}
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
    >
      {children}
      <span
        role="tooltip"
        className="pointer-events-none absolute left-1/2 top-full z-30 mt-1.5 -translate-x-1/2 whitespace-nowrap rounded-md bg-neutral-900 px-2 py-1 text-[11px] font-medium text-white opacity-0 shadow-lg transition-opacity group-hover/tip:opacity-100 group-focus-visible/tip:opacity-100"
      >
        {label}
      </span>
    </button>
  );
}

function CopyField({ isLight, label, value }: { isLight: boolean; label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div>
      <p className={labelClass(isLight)}>{label}</p>
      <div className="flex gap-2">
        <input className={`${fieldClass(isLight)} font-mono text-xs`} readOnly value={value} />
        <button
          type="button"
          className={ghostBtn(isLight)}
          onClick={() => {
            void navigator.clipboard.writeText(value);
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1200);
          }}
        >
          {copied ? <Check size={14} /> : <Copy size={14} />}
        </button>
      </div>
    </div>
  );
}
